<template>
    <form v-on:submit.prevent="saveForm" novalidate>
        <section
            class="bg-white px-4 py-5 border-b border-gray-200 sm:px-6 shadow my-8 mx-4 rounded-lg"
        >
            <div
                class="md:flex md:items-center md:justify-between md:space-x-5"
                :class="{ 'opacity-30': form.delete }"
            >
                <div class="flex items-start space-x-5">
                    <div class="flex-shrink-0">
                        <div
                            class="hidden relative rounded-full overflow-hidden lg:block"
                        >
                            <img
                                class="relative rounded-full w-20 h-20"
                                :alt="form.initials"
                                v-if="form.avatar"
                                :src="form.avatar"
                            />
                            <span
                                class="inline-flex items-center justify-center h-20 w-20 rounded-full bg-blue-500"
                                v-if="!form.avatar"
                            >
                                <span
                                    class="font-medium text-2xl leading-none text-white"
                                    >{{ form.initials }}</span
                                >
                            </span>
                            <span
                                class="absolute inset-0 shadow-inner rounded-full"
                                aria-hidden="true"
                            />

                            <label
                                for="logo"
                                class="absolute inset-0 w-full h-full bg-black bg-opacity-75 flex items-center justify-center text-sm font-medium text-white opacity-0 hover:opacity-100 focus-within:opacity-100"
                            >
                                <span>
                                    <PencilIcon
                                        class="h-8 w-8 text-white inline hover:text-blue-50 cursor-pointer"
                                        aria-hidden="true"
                                    />
                                </span>

                                <input
                                    id="logo"
                                    v-show="!form.logo"
                                    ref="logo"
                                    accept="image/*"
                                    v-on:change="handleFileUpload('logo')"
                                    type="file"
                                    class="absolute inset-0 w-full h-full opacity-0 cursor-pointer border-gray-300 rounded-md"
                                />
                            </label>
                        </div>
                    </div>
                    <div class="pt-1.5">
                        <h1 class="text-2xl font-bold text-gray-900">
                            {{ form.name }} {{ form.lastname }}
                        </h1>
                        <p class="text-sm font-medium text-gray-500 mt-1">
                            <span
                                class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-700 text-blue-50"
                                >{{
                                    t(
                                        "meetings.tag.voting-weight",
                                        "Stemgewicht:"
                                    )
                                }}
                                {{ form.voting_weight }}</span
                            >

                            <span
                                class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 ml-2"
                                v-for="(tag, id) in form.tags"
                                v-bind:key="id"
                                >{{ tag }}</span
                            >
                        </p>
                        <router-link
                            v-if="!form.id"
                            :to="{
                                name: 'members-upload',
                            }"
                            replace
                            class="inline-flex items-center px-5 py-2 border border-green-700 rounded-md shadow text-base font-semibold text-green-50 bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 absolute right-20 float-right -mr-4 -mt-4 ml-2"
                        >
                            <svg
                                class="fill-current w-6 h-6 mr-2"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    stroke-width="2"
                                    d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                                ></path>
                            </svg>

                            {{
                                t(
                                    "members.import.excel-create",
                                    "Upload Excel "
                                )
                            }}
                        </router-link>
                    </div>
                </div>
                <div
                    class="mt-6 flex flex-col-reverse justify-stretch space-y-4 space-y-reverse sm:flex-row-reverse sm:justify-end sm:space-x-reverse sm:space-y-0 sm:space-x-3 md:mt-0 md:flex-row md:space-x-3 hidden"
                >
                    <button
                        type="button"
                        class="inline-flex justify-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500"
                    >
                        <MailIcon
                            class="-ml-1 mr-2 h-5 w-5 text-gray-400"
                            aria-hidden="true"
                        />
                        <span>Mail</span>
                    </button>
                    <button
                        type="button"
                        class="inline-flex justify-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500"
                    >
                        <PhoneIcon
                            class="-ml-1 mr-2 h-5 w-5 text-gray-400"
                            aria-hidden="true"
                        />
                        <span>Bel</span>
                    </button>
                </div>
            </div>

            <div class="pt-8">
                <article class="grid lg:grid-cols-2 gap-16">
                    <section :class="{ 'opacity-30': form.delete }">
                        <div>
                            <h3
                                class="text-lg leading-6 font-medium text-gray-900"
                            >
                                {{
                                    t(
                                        "meetings.form.title-info",
                                        "Persoonlijke info"
                                    )
                                }}
                            </h3>
                            <p class="py-1 text-sm text-gray-500">
                                {{
                                    t("meetings.form.title-text", "toelichting")
                                }}
                            </p>
                        </div>
                        <div
                            class="mt-3 grid grid-cols-1 gap-y-2 gap-x-4 sm:grid-cols-6"
                        >
                            <div class="col-span-6 md:col-span-3">
                                <label
                                    class="block text-sm font-medium text-gray-700"
                                    >{{
                                        t("form-member.name", "Voornaam")
                                    }}</label
                                >
                                <div class="mt-1 relative rounded-md shadow-sm">
                                    <input
                                        type="text"
                                        class="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                        :class="{
                                            'border-red-300 text-red-900 focus:ring-red-500 focus:border-red-500':
                                                getError('name'),
                                        }"
                                        @keypress="errors = null"
                                        v-model="form.name"
                                    />
                                    <div
                                        class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
                                    >
                                        <ExclamationCircleIcon
                                            v-if="getError('name')"
                                            class="h-5 w-5 text-red-500"
                                            aria-hidden="true"
                                        />
                                    </div>
                                </div>
                                <p
                                    class="mt-2 text-xs text-red-600"
                                    v-if="getError('name')"
                                >
                                    {{ getError("name", true) }}
                                </p>
                            </div>

                            <div class="col-span-6 md:col-span-3">
                                <label
                                    class="block text-sm font-medium text-gray-700"
                                    >{{
                                        t("form-member.lastname", "Achternaam")
                                    }}</label
                                >
                                <div class="mt-1 relative rounded-md shadow-sm">
                                    <input
                                        type="text"
                                        class="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                        :class="{
                                            'border-red-300 text-red-900 focus:ring-red-500 focus:border-red-500':
                                                getError('lastname'),
                                        }"
                                        @keypress="errors = null"
                                        v-model="form.lastname"
                                    />
                                    <div
                                        class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
                                    >
                                        <ExclamationCircleIcon
                                            v-if="getError('lastname')"
                                            class="h-5 w-5 text-red-500"
                                            aria-hidden="true"
                                        />
                                    </div>
                                </div>
                                <p
                                    class="mt-2 text-xs text-red-600"
                                    v-if="getError('lastname')"
                                >
                                    {{ getError("lastname", true) }}
                                </p>
                            </div>

                            <div class="col-span-6 md:col-span-6">
                                <label
                                    class="block text-sm font-medium text-gray-700"
                                    >{{
                                        t("form-member.email", "E-mailadres")
                                    }}</label
                                >
                                <div class="mt-1 relative rounded-md shadow-sm">
                                    <input
                                        type="text"
                                        class="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                        :class="{
                                            'border-red-300 text-red-900 focus:ring-red-500 focus:border-red-500':
                                                getError('email'),
                                            'cursor-not-allowed':
                                                form &&
                                                form.id &&
                                                !getError('email'),
                                        }"
                                        no-keydown="errors = null"
                                        v-model="form.email"
                                        :disabled="
                                            form.id && !getError('email')
                                        "
                                        :readonly="
                                            form.id && !getError('email')
                                        "
                                    />
                                    <div
                                        class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
                                    >
                                        <ExclamationCircleIcon
                                            v-if="getError('email')"
                                            class="h-5 w-5 text-red-500"
                                            aria-hidden="true"
                                        />
                                    </div>
                                </div>
                                <p
                                    class="mt-2 text-xs text-red-600"
                                    v-if="getError('email')"
                                >
                                    {{ getError("email", true) }}
                                </p>
                            </div>

                            <div class="col-span-6 md:col-span-3">
                                <label
                                    class="block text-sm font-medium text-gray-700"
                                    >{{
                                        t("form-member.phone", "Telefoonnummer")
                                    }}</label
                                >
                                <div class="mt-1 relative rounded-md shadow-sm">
                                    <input
                                        type="tel"
                                        class="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                        :class="{
                                            'border-red-300 text-red-900 focus:ring-red-500 focus:border-red-500':
                                                getError('phone'),
                                        }"
                                        @keypress="errors = null"
                                        v-model="form.phone"
                                    />
                                    <div
                                        class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
                                    >
                                        <ExclamationCircleIcon
                                            v-if="getError('phone')"
                                            class="h-5 w-5 text-red-500"
                                            aria-hidden="true"
                                        />
                                    </div>
                                </div>
                                <p
                                    class="mt-2 text-xs text-red-600"
                                    v-if="getError('phone')"
                                >
                                    {{ getError("phone", true) }}
                                </p>
                            </div>

                            <div class="col-span-6 md:col-span-3">
                                <label
                                    class="block text-sm font-medium text-gray-700"
                                    >{{
                                        t(
                                            "form-member.birthday",
                                            "Geboortedatum"
                                        )
                                    }}</label
                                >
                                <div class="mt-1 relative rounded-md shadow-sm">
                                    <input
                                        class="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                        :class="{
                                            'border-red-300 text-red-900 focus:ring-red-500 focus:border-red-500':
                                                getError('birthday'),
                                        }"
                                        @keypress="errors = null"
                                        v-model="form.birthday"
                                        type="tel"
                                        v-mask="'##-##-####'"
                                    />
                                    <div
                                        class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
                                    >
                                        <ExclamationCircleIcon
                                            v-if="getError('birthday')"
                                            class="h-5 w-5 text-red-500"
                                            aria-hidden="true"
                                        />
                                    </div>
                                </div>
                                <p
                                    class="mt-2 text-xs text-red-600"
                                    v-if="getError('birthday')"
                                >
                                    {{ getError("birthday", true) }}
                                </p>
                            </div>
                        </div>

                        <article v-if="form.address">
                            <div class="mt-8">
                                <h3
                                    class="text-lg leading-6 font-medium text-gray-900"
                                >
                                    {{
                                        t(
                                            "meetings.form.title-address",
                                            "Adres"
                                        )
                                    }}
                                </h3>
                                <p class="py-1 text-sm text-gray-500">
                                    {{
                                        t(
                                            "meetings.form.text-address",
                                            "Adresgegevens toelichting"
                                        )
                                    }}
                                </p>
                            </div>
                            <div
                                class="mt-3 grid grid-cols-1 gap-y-2 gap-x-4 sm:grid-cols-6"
                            >
                                <div
                                    class="col-span-4 md:col-span-4"
                                    v-show="
                                        edit.id ||
                                        form.address.street ||
                                        lang != 'nl'
                                    "
                                >
                                    <label
                                        class="block text-sm font-medium text-gray-700"
                                        >{{
                                            t(
                                                "form-member.street",
                                                "Straatnaam"
                                            )
                                        }}</label
                                    >
                                    <div
                                        class="mt-1 relative rounded-md shadow-sm"
                                    >
                                        <input
                                            type="text"
                                            class="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                            :class="{
                                                'border-red-300 text-red-900 focus:ring-red-500 focus:border-red-500':
                                                    getError('address.street'),
                                            }"
                                            @keypress="errors = null"
                                            v-model="form.address.street"
                                        />
                                        <div
                                            class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
                                        >
                                            <ExclamationCircleIcon
                                                v-if="
                                                    getError('address.street')
                                                "
                                                class="h-5 w-5 text-red-500"
                                                aria-hidden="true"
                                            />
                                        </div>
                                    </div>
                                    <p
                                        class="mt-2 text-xs text-red-600"
                                        v-if="getError('address.street')"
                                    >
                                        {{ getError("address.street", true) }}
                                    </p>
                                </div>

                                <div
                                    class="col-span-1 md:col-span-1"
                                    v-show="
                                        edit.id ||
                                        form.address.street ||
                                        lang != 'nl'
                                    "
                                >
                                    <label
                                        class="block text-sm font-medium text-gray-700"
                                        >{{
                                            t(
                                                "form-member.housenumber",
                                                "Huisnr."
                                            )
                                        }}</label
                                    >
                                    <div
                                        class="mt-1 relative rounded-md shadow-sm"
                                    >
                                        <input
                                            type="text"
                                            class="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                            :class="{
                                                'border-red-300 text-red-900 focus:ring-red-500 focus:border-red-500':
                                                    getError(
                                                        'address.housenumber'
                                                    ),
                                            }"
                                            no-input="debounceSearchAddress"
                                            @keypress="errors = null"
                                            v-model="form.address.housenumber"
                                        />
                                        <div
                                            class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
                                        >
                                            <ExclamationCircleIcon
                                                v-if="
                                                    getError(
                                                        'address.housenumber'
                                                    )
                                                "
                                                class="h-5 w-5 text-red-500"
                                                aria-hidden="true"
                                            />
                                        </div>
                                    </div>
                                    <p
                                        class="mt-2 text-xs text-red-600"
                                        v-if="getError('address.housenumber')"
                                    >
                                        {{
                                            getError(
                                                "address.housenumber",
                                                true
                                            )
                                        }}
                                    </p>
                                </div>

                                <div class="col-span-6 md:col-span-2">
                                    <label
                                        class="block text-sm font-medium text-gray-700"
                                        >{{
                                            t(
                                                "form-member.postcode",
                                                "Postcode"
                                            )
                                        }}</label
                                    >
                                    <div
                                        class="mt-1 relative rounded-md shadow-sm"
                                    >
                                        <input
                                            type="text"
                                            class="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                            :class="{
                                                'border-red-300 text-red-900 focus:ring-red-500 focus:border-red-500':
                                                    getError(
                                                        'address.postcode'
                                                    ),
                                            }"
                                            @input="debounceSearchAddress"
                                            @keypress="errors = null"
                                            v-model="form.address.postcode"
                                        />
                                        <div
                                            class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
                                        >
                                            <ExclamationCircleIcon
                                                v-if="
                                                    getError('address.postcode')
                                                "
                                                class="h-5 w-5 text-red-500"
                                                aria-hidden="true"
                                            />
                                        </div>
                                    </div>
                                    <p
                                        class="mt-2 text-xs text-red-600"
                                        v-if="getError('address.postcode')"
                                    >
                                        {{ getError("address.postcode", true) }}
                                    </p>
                                </div>

                                <div
                                    class="col-span-6 md:col-span-3"
                                    v-show="
                                        edit.id ||
                                        form.address.street ||
                                        lang != 'nl'
                                    "
                                >
                                    <label
                                        class="block text-sm font-medium text-gray-700"
                                        >{{
                                            t("form-member.city", "Plaatsnaam")
                                        }}</label
                                    >
                                    <div
                                        class="mt-1 relative rounded-md shadow-sm"
                                    >
                                        <input
                                            type="text"
                                            class="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                            :class="{
                                                'border-red-300 text-red-900 focus:ring-red-500 focus:border-red-500':
                                                    getError('address.city'),
                                            }"
                                            @keypress="errors = null"
                                            v-model="form.address.city"
                                        />
                                        <div
                                            class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
                                        >
                                            <ExclamationCircleIcon
                                                v-if="getError('address.city')"
                                                class="h-5 w-5 text-red-500"
                                                aria-hidden="true"
                                            />
                                        </div>
                                    </div>
                                    <p
                                        class="mt-2 text-xs text-red-600"
                                        v-if="getError('address.city')"
                                    >
                                        {{ getError("address.city", true) }}
                                    </p>
                                </div>
                            </div>
                        </article>

                        <article>
                            <div class="mt-6">
                                <h3
                                    class="text-lg leading-6 font-medium text-gray-900"
                                >
                                    {{
                                        t(
                                            "meetings.form.title-meetings",
                                            "Vergaderingen"
                                        )
                                    }}
                                </h3>
                                <p class="py-1 text-sm text-gray-500">
                                    {{
                                        t(
                                            "meetings.form.text-meetings",
                                            "Details voor vergaderingen hier noteren"
                                        )
                                    }}
                                </p>
                            </div>
                            <div
                                class="mt-3 grid grid-cols-1 gap-y-2 gap-x-4 sm:grid-cols-6"
                            >
                                <div class="col-span-6 md:col-span-2">
                                    <label
                                        class="block text-sm font-medium text-gray-700"
                                        >{{
                                            t(
                                                "form-member.voting_weight",
                                                "Stemgewicht"
                                            )
                                        }}</label
                                    >
                                    <div
                                        class="mt-1 relative rounded-md shadow-sm"
                                    >
                                        <input
                                            type="number"
                                            min="0"
                                            step="1"
                                            class="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                            :class="{
                                                'border-red-300 text-red-900 focus:ring-red-500 focus:border-red-500':
                                                    getError('voting_weight'),
                                            }"
                                            @keypress="errors = null"
                                            v-model="form.voting_weight"
                                        />
                                        <div
                                            class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
                                        >
                                            <ExclamationCircleIcon
                                                v-if="getError('voting_weight')"
                                                class="h-5 w-5 text-red-500"
                                                aria-hidden="true"
                                            />
                                        </div>
                                    </div>
                                    <p
                                        class="mt-2 text-xs text-red-600"
                                        v-if="getError('voting_weight')"
                                    >
                                        {{ getError("voting_weight", true) }}
                                    </p>
                                </div>

                                <div class="col-span-6 md:col-span-6">
                                    <SwitchGroup
                                        as="div"
                                        class="flex items-center mt-4"
                                    >
                                        <Switch
                                            v-model="form.is_moderator"
                                            @click="
                                                form.is_moderator
                                                    ? (form.can_publish = true)
                                                    : (form.can_publish =
                                                          form.can_publish)
                                            "
                                            :class="[
                                                form.is_moderator
                                                    ? 'bg-blue-600'
                                                    : 'bg-gray-200',
                                                'relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500',
                                            ]"
                                        >
                                            <span
                                                :class="[
                                                    form.is_moderator
                                                        ? 'translate-x-5'
                                                        : 'translate-x-0',
                                                    'pointer-events-none relative inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200',
                                                ]"
                                            >
                                                <span
                                                    :class="[
                                                        form.is_moderator
                                                            ? 'opacity-0 ease-out duration-100'
                                                            : 'opacity-100 ease-in duration-200',
                                                        'absolute inset-0 h-full w-full flex items-center justify-center transition-opacity',
                                                    ]"
                                                    aria-hidden="true"
                                                >
                                                    <svg
                                                        class="h-3 w-3 text-gray-400"
                                                        fill="none"
                                                        viewBox="0 0 12 12"
                                                    >
                                                        <path
                                                            d="M4 8l2-2m0 0l2-2M6 6L4 4m2 2l2 2"
                                                            stroke="currentColor"
                                                            stroke-width="2"
                                                            stroke-linecap="round"
                                                            stroke-linejoin="round"
                                                        />
                                                    </svg>
                                                </span>
                                                <span
                                                    :class="[
                                                        form.is_moderator
                                                            ? 'opacity-100 ease-in duration-200'
                                                            : 'opacity-0 ease-out duration-100',
                                                        'absolute inset-0 h-full w-full flex items-center justify-center transition-opacity',
                                                    ]"
                                                    aria-hidden="true"
                                                >
                                                    <svg
                                                        class="h-3 w-3 text-blue-600"
                                                        fill="currentColor"
                                                        viewBox="0 0 12 12"
                                                    >
                                                        <path
                                                            d="M3.707 5.293a1 1 0 00-1.414 1.414l1.414-1.414zM5 8l-.707.707a1 1 0 001.414 0L5 8zm4.707-3.293a1 1 0 00-1.414-1.414l1.414 1.414zm-7.414 2l2 2 1.414-1.414-2-2-1.414 1.414zm3.414 2l4-4-1.414-1.414-4 4 1.414 1.414z"
                                                        />
                                                    </svg>
                                                </span>
                                            </span>
                                        </Switch>

                                        <SwitchLabel as="span" class="ml-3">
                                            <span
                                                class="text-sm font-medium text-gray-900"
                                                >{{
                                                    t(
                                                        "form-member.moderator-rights",
                                                        "Moderator-rechten"
                                                    )
                                                }}</span
                                            >
                                            <!-- <span class="text-sm text-gray-500">(Save 10%)</span> -->
                                        </SwitchLabel>
                                    </SwitchGroup>
                                </div>

                                <div class="col-span-6 md:col-span-6">
                                    <SwitchGroup
                                        as="div"
                                        class="flex items-center mt-4"
                                        :class="{
                                            'opacity-70 disabled pointer-events-none':
                                                form.is_moderator,
                                        }"
                                    >
                                        <Switch
                                            v-model="form.can_publish"
                                            class="relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                            :class="{
                                                'bg-blue-600': form.can_publish,
                                                'bg-gray-200':
                                                    !form.can_publish,
                                                'opacity-70 disabled pointer-events-none':
                                                    form.is_moderator,
                                            }"
                                        >
                                            <div
                                                :class="{
                                                    'opacity-70 disabled pointer-events-none':
                                                        form.is_moderator,
                                                }"
                                            >
                                                <span
                                                    :class="[
                                                        form.can_publish
                                                            ? 'translate-x-5'
                                                            : 'translate-x-0',
                                                        'pointer-events-none relative inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200',
                                                    ]"
                                                >
                                                    <span
                                                        :class="[
                                                            form.can_publish
                                                                ? 'opacity-0 ease-out duration-100'
                                                                : 'opacity-100 ease-in duration-200',
                                                            'absolute inset-0 h-full w-full flex items-center justify-center transition-opacity',
                                                        ]"
                                                        aria-hidden="true"
                                                    >
                                                        <svg
                                                            class="h-3 w-3 text-gray-400"
                                                            fill="none"
                                                            viewBox="0 0 12 12"
                                                        >
                                                            <path
                                                                d="M4 8l2-2m0 0l2-2M6 6L4 4m2 2l2 2"
                                                                stroke="currentColor"
                                                                stroke-width="2"
                                                                stroke-linecap="round"
                                                                stroke-linejoin="round"
                                                            />
                                                        </svg>
                                                    </span>
                                                    <span
                                                        :class="[
                                                            form.can_publish
                                                                ? 'opacity-100 ease-in duration-200'
                                                                : 'opacity-0 ease-out duration-100',
                                                            'absolute inset-0 h-full w-full flex items-center justify-center transition-opacity',
                                                        ]"
                                                        aria-hidden="true"
                                                    >
                                                        <svg
                                                            class="h-3 w-3 text-blue-600"
                                                            fill="currentColor"
                                                            viewBox="0 0 12 12"
                                                        >
                                                            <path
                                                                d="M3.707 5.293a1 1 0 00-1.414 1.414l1.414-1.414zM5 8l-.707.707a1 1 0 001.414 0L5 8zm4.707-3.293a1 1 0 00-1.414-1.414l1.414 1.414zm-7.414 2l2 2 1.414-1.414-2-2-1.414 1.414zm3.414 2l4-4-1.414-1.414-4 4 1.414 1.414z"
                                                            />
                                                        </svg>
                                                    </span>
                                                </span>
                                            </div>
                                        </Switch>

                                        <SwitchLabel
                                            as="span"
                                            class="ml-3"
                                            :class="{
                                                'opacity-70 disabled pointer-events-none':
                                                    form.is_moderator,
                                            }"
                                        >
                                            <span
                                                class="text-sm font-medium text-gray-900"
                                                :class="{
                                                    'opacity-70 disabled pointer-events-none':
                                                        form.is_moderator,
                                                }"
                                                >{{
                                                    t(
                                                        "form-member.publish-rights",
                                                        "Uitzend-rechten"
                                                    )
                                                }}</span
                                            >
                                            <!-- <span class="text-sm text-gray-500">(kan deelnemen met de webcam tijdens vergadering)</span> -->
                                        </SwitchLabel>
                                    </SwitchGroup>
                                </div>

                                <div class="col-span-6 md:col-span-6">
                                    <SwitchGroup
                                        as="div"
                                        class="flex items-center mt-3"
                                        v-show="edit.id && !form.is_blocked"
                                    >
                                        <Switch
                                            v-model="form.has_pin"
                                            :class="[
                                                form.has_pin
                                                    ? 'bg-blue-600'
                                                    : 'bg-gray-200',
                                                'relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500',
                                            ]"
                                        >
                                            <span
                                                :class="[
                                                    form.has_pin
                                                        ? 'translate-x-5'
                                                        : 'translate-x-0',
                                                    'pointer-events-none relative inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200',
                                                ]"
                                            >
                                                <span
                                                    :class="[
                                                        form.has_pin
                                                            ? 'opacity-0 ease-out duration-100'
                                                            : 'opacity-100 ease-in duration-200',
                                                        'absolute inset-0 h-full w-full flex items-center justify-center transition-opacity',
                                                    ]"
                                                    aria-hidden="true"
                                                >
                                                    <svg
                                                        class="h-3 w-3 text-gray-400"
                                                        fill="none"
                                                        viewBox="0 0 12 12"
                                                    >
                                                        <path
                                                            d="M4 8l2-2m0 0l2-2M6 6L4 4m2 2l2 2"
                                                            stroke="currentColor"
                                                            stroke-width="2"
                                                            stroke-linecap="round"
                                                            stroke-linejoin="round"
                                                        />
                                                    </svg>
                                                </span>
                                                <span
                                                    :class="[
                                                        form.has_pin
                                                            ? 'opacity-100 ease-in duration-200'
                                                            : 'opacity-0 ease-out duration-100',
                                                        'absolute inset-0 h-full w-full flex items-center justify-center transition-opacity',
                                                    ]"
                                                    aria-hidden="true"
                                                >
                                                    <svg
                                                        class="h-3 w-3 text-blue-600"
                                                        fill="currentColor"
                                                        viewBox="0 0 12 12"
                                                    >
                                                        <path
                                                            d="M3.707 5.293a1 1 0 00-1.414 1.414l1.414-1.414zM5 8l-.707.707a1 1 0 001.414 0L5 8zm4.707-3.293a1 1 0 00-1.414-1.414l1.414 1.414zm-7.414 2l2 2 1.414-1.414-2-2-1.414 1.414zm3.414 2l4-4-1.414-1.414-4 4 1.414 1.414z"
                                                        />
                                                    </svg>
                                                </span>
                                            </span>
                                        </Switch>

                                        <SwitchLabel as="span" class="ml-3">
                                            <span
                                                class="text-sm font-medium text-gray-900"
                                                >{{
                                                    t(
                                                        "form-member.add-access-code",
                                                        "Toegangscode koppelen"
                                                    )
                                                }}</span
                                            >
                                            <!-- <span class="text-sm text-gray-500">(Save 10%)</span> -->
                                        </SwitchLabel>
                                    </SwitchGroup>
                                </div>
                                <div class="col-span-6 md:col-span-2">
                                    <section
                                        v-show="
                                            form.has_pin && !form.is_blocked
                                        "
                                    >
                                        <label
                                            class="block text-sm font-medium text-gray-700 mt-2"
                                            >{{
                                                t(
                                                    "form-member.access-code",
                                                    "Toegangscode"
                                                )
                                            }}</label
                                        >
                                        <div
                                            class="mt-1 relative rounded-md shadow-sm"
                                        >
                                            <input
                                                type="number"
                                                min="0"
                                                step="1"
                                                class="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                                readonly
                                                disabled
                                                v-model="form.pincode"
                                            />
                                        </div>
                                    </section>
                                </div>
                            </div>
                        </article>

                        <article v-if="form.ice">
                            <div class="mt-8">
                                <h3
                                    class="text-lg leading-6 font-medium text-gray-900"
                                >
                                    {{ t("meetings.form.title-ice", "ICE") }}
                                </h3>
                                <p class="py-1 text-sm text-gray-500">
                                    {{
                                        t(
                                            "meetings.form.text-ice",
                                            "In Case of Emergency details"
                                        )
                                    }}
                                </p>
                            </div>
                            <div
                                class="mt-3 grid grid-cols-1 gap-y-2 gap-x-4 sm:grid-cols-6"
                            >
                                <div class="col-span-6 md:col-span-3">
                                    <label
                                        class="block text-sm font-medium text-gray-700"
                                        >{{
                                            t("form-member.ice-name", "Naam")
                                        }}</label
                                    >
                                    <div
                                        class="mt-1 relative rounded-md shadow-sm"
                                    >
                                        <input
                                            type="text"
                                            class="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                            :class="{
                                                'border-red-300 text-red-900 focus:ring-red-500 focus:border-red-500':
                                                    getError('ice.name'),
                                            }"
                                            @keypress="errors = null"
                                            v-model="form.ice.name"
                                        />
                                        <div
                                            class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
                                        >
                                            <ExclamationCircleIcon
                                                v-if="getError('ice.name')"
                                                class="h-5 w-5 text-red-500"
                                                aria-hidden="true"
                                            />
                                        </div>
                                    </div>
                                    <p
                                        class="mt-2 text-xs text-red-600"
                                        v-if="getError('ice.name')"
                                    >
                                        {{ getError("ice.name", true) }}
                                    </p>
                                </div>

                                <div class="col-span-6 md:col-span-3">
                                    <label
                                        class="block text-sm font-medium text-gray-700"
                                        >{{
                                            t(
                                                "form-member.ice-phone",
                                                "Telefoonnummer"
                                            )
                                        }}</label
                                    >
                                    <div
                                        class="mt-1 relative rounded-md shadow-sm"
                                    >
                                        <input
                                            type="tel"
                                            class="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                            :class="{
                                                'border-red-300 text-red-900 focus:ring-red-500 focus:border-red-500':
                                                    getError('ice.phone'),
                                            }"
                                            @keypress="errors = null"
                                            v-model="form.ice.phone"
                                        />
                                        <div
                                            class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
                                        >
                                            <ExclamationCircleIcon
                                                v-if="getError('ice.phone')"
                                                class="h-5 w-5 text-red-500"
                                                aria-hidden="true"
                                            />
                                        </div>
                                    </div>
                                    <p
                                        class="mt-2 text-xs text-red-600"
                                        v-if="getError('ice.phone')"
                                    >
                                        {{ getError("ice.phone", true) }}
                                    </p>
                                </div>
                            </div>
                        </article>
                    </section>
                    <section>
                        <article
                            v-if="form.iban"
                            :class="{ 'opacity-30': form.delete }"
                        >
                            <div class="mt-0">
                                <h3
                                    class="text-lg leading-6 font-medium text-gray-900"
                                >
                                    {{
                                        t(
                                            "meetings.form.title-membership",
                                            "Lidmaatschap"
                                        )
                                    }}
                                </h3>
                                <p class="py-1 text-sm text-gray-500">
                                    {{
                                        t(
                                            "meetings.form.text-membership",
                                            "Bankdetails"
                                        )
                                    }}
                                </p>
                            </div>
                            <div
                                class="mt-3 grid grid-cols-1 gap-y-2 gap-x-4 sm:grid-cols-6"
                            >
                                <div class="col-span-6 md:col-span-3">
                                    <label
                                        class="block text-sm font-medium text-gray-700"
                                        >{{
                                            t(
                                                "form-member.iban-name",
                                                "Rekeninghouder"
                                            )
                                        }}</label
                                    >
                                    <div
                                        class="mt-1 relative rounded-md shadow-sm"
                                    >
                                        <input
                                            type="text"
                                            class="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                            :class="{
                                                'border-red-300 text-red-900 focus:ring-red-500 focus:border-red-500':
                                                    getError(
                                                        'iban.consumerName'
                                                    ),
                                            }"
                                            @keypress="errors = null"
                                            v-model="form.iban.consumerName"
                                        />
                                        <div
                                            class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
                                        >
                                            <ExclamationCircleIcon
                                                v-if="
                                                    getError(
                                                        'iban.consumerName'
                                                    )
                                                "
                                                class="h-5 w-5 text-red-500"
                                                aria-hidden="true"
                                            />
                                        </div>
                                    </div>
                                    <p
                                        class="mt-2 text-xs text-red-600"
                                        v-if="getError('iban.consumerName')"
                                    >
                                        {{
                                            getError("iban.consumerName", true)
                                        }}
                                    </p>
                                </div>
                                <div class="col-span-6 md:col-span-3">
                                    <label
                                        class="block text-sm font-medium text-gray-700"
                                        >{{
                                            t(
                                                "form-member.iban-account",
                                                "IBAN"
                                            )
                                        }}</label
                                    >
                                    <div
                                        class="mt-1 relative rounded-md shadow-sm"
                                    >
                                        <input
                                            type="text"
                                            class="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                                            :class="{
                                                'border-red-300 text-red-900 focus:ring-red-500 focus:border-red-500':
                                                    getError(
                                                        'iban.consumerAccount'
                                                    ),
                                            }"
                                            mask="AA## #### #### #### #### #### ###"
                                            @keypress="errors = null"
                                            @input="
                                                forceUpper(
                                                    $event,
                                                    form.iban,
                                                    'consumerAccount'
                                                )
                                            "
                                            v-model="form.iban.consumerAccount"
                                        />
                                        <div
                                            class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
                                        >
                                            <ExclamationCircleIcon
                                                v-if="
                                                    getError(
                                                        'iban.consumerAccount'
                                                    )
                                                "
                                                class="h-5 w-5 text-red-500"
                                                aria-hidden="true"
                                            />
                                        </div>
                                    </div>
                                    <p
                                        class="mt-2 text-xs text-red-600"
                                        v-if="getError('iban.consumerAccount')"
                                    >
                                        {{
                                            getError(
                                                "iban.consumerAccount",
                                                true
                                            )
                                        }}
                                    </p>
                                </div>
                            </div>
                        </article>

                        <article
                            class="mt-0"
                            :class="{ 'opacity-30': form.delete }"
                        >
                            <fieldset class="mt-6" v-if="roles">
                                <div>
                                    <legend
                                        class="text-base font-medium text-gray-900"
                                    >
                                        {{
                                            t(
                                                "meetings.form.title-roles-group",
                                                "Rollen"
                                            )
                                        }}

                                        <PencilIcon
                                            @click="setModal('groups')"
                                            class="h-5 w-5 text-gray-500 inline hover:text-blue-600 cursor-pointer"
                                            aria-hidden="true"
                                        />
                                    </legend>
                                    <p class="text-sm text-gray-500">
                                        {{
                                            t(
                                                "meetings.form.text-roles-group",
                                                "Tekst voopr Rollen"
                                            )
                                        }}
                                    </p>
                                </div>
                                <div class="mt-4 space-y-4">
                                    <div
                                        class="col-span-2"
                                        v-if="
                                            Object.values(roles.groups)
                                                .length == 0
                                        "
                                    >
                                        <a
                                            class="relative block border-2 border-gray-300 border-dashed rounded-lg p-12 text-center hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 w-1/2 md:w-1/3"
                                            @click="setModal('groups')"
                                        >
                                            <svg
                                                class="mx-auto h-12 w-12 text-gray-400"
                                                xmlns="http://www.w3.org/2000/svg"
                                                stroke="currentColor"
                                                fill="none"
                                                viewBox="0 0 48 48"
                                                aria-hidden="true"
                                            >
                                                <path
                                                    stroke-linecap="round"
                                                    stroke-linejoin="round"
                                                    stroke-width="2"
                                                    d="M8 14v20c0 4.418 7.163 8 16 8 1.381 0 2.721-.087 4-.252M8 14c0 4.418 7.163 8 16 8s16-3.582 16-8M8 14c0-4.418 7.163-8 16-8s16 3.582 16 8m0 0v14m0-4c0 4.418-7.163 8-16 8S8 28.418 8 24m32 10v6m0 0v6m0-6h6m-6 0h-6"
                                                />
                                            </svg>
                                            <span
                                                class="mt-2 block text-sm font-medium text-gray-900"
                                            >
                                                {{
                                                    t(
                                                        "meetings.form.text-roles-add",
                                                        "Rollen toevoegen"
                                                    )
                                                }}
                                            </span>
                                        </a>
                                    </div>

                                    <div
                                        class="flex items-center"
                                        v-for="(role, id) in roles.groups"
                                        v-bind:key="id"
                                    >
                                        <input
                                            :id="'role' + id"
                                            name="role"
                                            :value="id"
                                            v-model="form.groups"
                                            :checked="
                                                checkIfInGroup(id, 'groups')
                                            "
                                            type="checkbox"
                                            class="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 rounded"
                                        />

                                        <label
                                            :for="'role' + id"
                                            class="ml-3 block text-sm font-medium text-gray-700"
                                        >
                                            {{ role }}
                                        </label>
                                    </div>
                                </div>
                            </fieldset>
                        </article>

                        <article
                            class="mt-6"
                            :class="{ 'opacity-30': form.delete }"
                        >
                            <fieldset class="mt-6" v-if="roles">
                                <div>
                                    <legend
                                        class="text-base font-medium text-gray-900"
                                    >
                                        {{
                                            t(
                                                "meetings.form.title-roles-membertypes",
                                                "Groepen "
                                            )
                                        }}

                                        <PencilIcon
                                            @click="setModal('members')"
                                            class="h-5 w-5 text-gray-500 inline hover:text-blue-600 cursor-pointer"
                                            aria-hidden="true"
                                        />
                                    </legend>
                                    <p class="text-sm text-gray-500">
                                        {{
                                            t(
                                                "meetings.form.text-roles-membertypes",
                                                "Zoals bijv Bestuur of Commissies"
                                            )
                                        }}
                                    </p>
                                </div>
                                <div class="mt-4 space-y-4">
                                    <div
                                        class="col-span-2"
                                        v-if="
                                            Object.values(roles.membertypes)
                                                .length == 0
                                        "
                                    >
                                        <a
                                            class="relative block border-2 border-gray-300 border-dashed rounded-lg p-12 text-center hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 col-span-2 w-1/2 md:w-1/3"
                                            @click="setModal('members')"
                                        >
                                            <svg
                                                class="mx-auto h-12 w-12 text-gray-400"
                                                xmlns="http://www.w3.org/2000/svg"
                                                stroke="currentColor"
                                                fill="none"
                                                viewBox="0 0 48 48"
                                                aria-hidden="true"
                                            >
                                                <path
                                                    stroke-linecap="round"
                                                    stroke-linejoin="round"
                                                    stroke-width="2"
                                                    d="M8 14v20c0 4.418 7.163 8 16 8 1.381 0 2.721-.087 4-.252M8 14c0 4.418 7.163 8 16 8s16-3.582 16-8M8 14c0-4.418 7.163-8 16-8s16 3.582 16 8m0 0v14m0-4c0 4.418-7.163 8-16 8S8 28.418 8 24m32 10v6m0 0v6m0-6h6m-6 0h-6"
                                                />
                                            </svg>
                                            <span
                                                class="mt-2 block text-sm font-medium text-gray-900"
                                            >
                                                {{
                                                    t(
                                                        "meetings.form.text-roles-members-add",
                                                        "Groepen toevoegen"
                                                    )
                                                }}
                                            </span>
                                        </a>
                                    </div>

                                    <div
                                        class="flex items-center"
                                        v-for="(role, id) in roles.membertypes"
                                        v-bind:key="id"
                                    >
                                        <input
                                            :id="'role' + id"
                                            name="role"
                                            :value="id"
                                            v-model="form.membertypes"
                                            :checked="
                                                checkIfInGroup(id, 'members')
                                            "
                                            type="checkbox"
                                            class="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 rounded"
                                        />

                                        <label
                                            :for="'role' + id"
                                            class="ml-3 block text-sm font-medium text-gray-700"
                                        >
                                            {{ role }}
                                        </label>
                                    </div>
                                </div>
                            </fieldset>
                        </article>
                        <article v-if="edit.id">
                            <div class="mt-6">
                                <h3
                                    class="text-lg leading-6 font-medium text-gray-900"
                                >
                                    {{
                                        t(
                                            "meetings.form.title-manage",
                                            "Beheer"
                                        )
                                    }}
                                </h3>
                                <p class="py-1 text-sm text-gray-500">
                                    {{
                                        t(
                                            "meetings.form.text-manage",
                                            "Beperk of verwijder hier het lid"
                                        )
                                    }}
                                </p>
                            </div>
                            <div
                                class="mt-3 grid grid-cols-1 gap-y-2 gap-x-4 sm:grid-cols-6"
                            >
                                <div class="col-span-6 md:col-span-6">
                                    <SwitchGroup
                                        as="div"
                                        class="flex items-center mt-3"
                                        v-show="edit.id"
                                    >
                                        <Switch
                                            v-model="form.is_blocked"
                                            :class="[
                                                form.is_blocked
                                                    ? 'bg-red-600'
                                                    : 'bg-gray-200',
                                                'relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500',
                                            ]"
                                        >
                                            <span
                                                :class="[
                                                    form.is_blocked
                                                        ? 'translate-x-5'
                                                        : 'translate-x-0',
                                                    'pointer-events-none relative inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200',
                                                ]"
                                            >
                                                <span
                                                    :class="[
                                                        form.is_blocked
                                                            ? 'opacity-0 ease-out duration-100'
                                                            : 'opacity-100 ease-in duration-200',
                                                        'absolute inset-0 h-full w-full flex items-center justify-center transition-opacity',
                                                    ]"
                                                    aria-hidden="true"
                                                >
                                                    <svg
                                                        class="h-3 w-3 text-gray-400"
                                                        fill="none"
                                                        viewBox="0 0 12 12"
                                                    >
                                                        <path
                                                            d="M4 8l2-2m0 0l2-2M6 6L4 4m2 2l2 2"
                                                            stroke="currentColor"
                                                            stroke-width="2"
                                                            stroke-linecap="round"
                                                            stroke-linejoin="round"
                                                        />
                                                    </svg>
                                                </span>
                                                <span
                                                    :class="[
                                                        form.is_blocked
                                                            ? 'opacity-100 ease-in duration-200'
                                                            : 'opacity-0 ease-out duration-100',
                                                        'absolute inset-0 h-full w-full flex items-center justify-center transition-opacity',
                                                    ]"
                                                    aria-hidden="true"
                                                >
                                                    <svg
                                                        class="h-3 w-3 text-red-600"
                                                        fill="currentColor"
                                                        viewBox="0 0 12 12"
                                                    >
                                                        <path
                                                            d="M3.707 5.293a1 1 0 00-1.414 1.414l1.414-1.414zM5 8l-.707.707a1 1 0 001.414 0L5 8zm4.707-3.293a1 1 0 00-1.414-1.414l1.414 1.414zm-7.414 2l2 2 1.414-1.414-2-2-1.414 1.414zm3.414 2l4-4-1.414-1.414-4 4 1.414 1.414z"
                                                        />
                                                    </svg>
                                                </span>
                                            </span>
                                        </Switch>

                                        <SwitchLabel as="span" class="ml-3">
                                            <span
                                                class="text-sm font-medium text-gray-900"
                                                >{{
                                                    t(
                                                        "form-member.revoke-access",
                                                        "Toegang ontzeggen vergaderingen"
                                                    )
                                                }}</span
                                            >
                                            <!-- <span class="text-xs text-gray-500" v-if="form.is_blocked"
                    >(Deelnemer kan niet meer inloggen)</span
                > -->
                                        </SwitchLabel>
                                    </SwitchGroup>
                                </div>

                                <div class="col-span-6 md:col-span-6">
                                    <SwitchGroup
                                        as="div"
                                        class="flex items-center mt-3"
                                        v-show="edit.id"
                                        @click="checkDelete()"
                                    >
                                        <Switch
                                            v-model="form.delete"
                                            :class="[
                                                form.delete
                                                    ? 'bg-red-600'
                                                    : 'bg-gray-200',
                                                'relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500',
                                            ]"
                                        >
                                            <span
                                                :class="[
                                                    form.delete
                                                        ? 'translate-x-5'
                                                        : 'translate-x-0',
                                                    'pointer-events-none relative inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200',
                                                ]"
                                            >
                                                <span
                                                    :class="[
                                                        form.delete
                                                            ? 'opacity-0 ease-out duration-100'
                                                            : 'opacity-100 ease-in duration-200',
                                                        'absolute inset-0 h-full w-full flex items-center justify-center transition-opacity',
                                                    ]"
                                                    aria-hidden="true"
                                                >
                                                    <svg
                                                        class="h-3 w-3 text-gray-400"
                                                        fill="none"
                                                        viewBox="0 0 12 12"
                                                    >
                                                        <path
                                                            d="M4 8l2-2m0 0l2-2M6 6L4 4m2 2l2 2"
                                                            stroke="currentColor"
                                                            stroke-width="2"
                                                            stroke-linecap="round"
                                                            stroke-linejoin="round"
                                                        />
                                                    </svg>
                                                </span>
                                                <span
                                                    :class="[
                                                        form.delete
                                                            ? 'opacity-100 ease-in duration-200'
                                                            : 'opacity-0 ease-out duration-100',
                                                        'absolute inset-0 h-full w-full flex items-center justify-center transition-opacity',
                                                    ]"
                                                    aria-hidden="true"
                                                >
                                                    <svg
                                                        class="h-3 w-3 text-red-600"
                                                        fill="currentColor"
                                                        viewBox="0 0 12 12"
                                                    >
                                                        <path
                                                            d="M3.707 5.293a1 1 0 00-1.414 1.414l1.414-1.414zM5 8l-.707.707a1 1 0 001.414 0L5 8zm4.707-3.293a1 1 0 00-1.414-1.414l1.414 1.414zm-7.414 2l2 2 1.414-1.414-2-2-1.414 1.414zm3.414 2l4-4-1.414-1.414-4 4 1.414 1.414z"
                                                        />
                                                    </svg>
                                                </span>
                                            </span>
                                        </Switch>

                                        <SwitchLabel as="span" class="ml-3">
                                            <span
                                                class="text-sm font-medium text-gray-900"
                                                >{{
                                                    t(
                                                        "form-member.delete-user",
                                                        "Verwijder deelnemer"
                                                    )
                                                }}</span
                                            >
                                            <span
                                                class="text-xs text-red-700 block"
                                                v-if="form.delete"
                                                >{{
                                                    t(
                                                        "form-member.delete-confirm",
                                                        "Kan niet ongedaan gemaakt worden"
                                                    )
                                                }}</span
                                            >
                                        </SwitchLabel>
                                    </SwitchGroup>
                                </div>
                            </div>
                        </article>
                    </section>
                </article>
            </div>

            <div class="mt-3 sm:mt-6">
                <button
                    type="submit"
                    class="inline-flex justify-center w-full rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:text-sm disabled:opacity-50"
                    :class="{
                        'bg-red-600 text-red-50 hover:bg-red-700': form.delete,
                        'bg-blue-600 text-blue-50 hover:bg-blue-700':
                            !form.delete,
                    }"
                    :disabled="loading"
                >
                    <svg
                        v-if="loading"
                        class="animate-spin h-5 w-5 mr-4"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                    >
                        <circle
                            class="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            stroke-width="4"
                        ></circle>
                        <path
                            class="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                    </svg>
                    {{ t("save", "Opslaan") }}
                </button>
            </div>
        </section>
    </form>
</template>

<script>
import {
    ExclamationCircleIcon,
    XIcon,
    TrashIcon,
    PencilIcon,
    MailIcon,
    PhoneIcon,
} from "@heroicons/vue/solid";

import { Switch, SwitchGroup, SwitchLabel } from "@headlessui/vue";
export default {
    components: {
        ExclamationCircleIcon,
        Switch,
        SwitchGroup,
        SwitchLabel,
        XIcon,
        TrashIcon,
        MailIcon,
        PhoneIcon,
        PencilIcon,
    },
    mounted() {
        if (this.edit) {
            this.form = this.edit;
        }
        Eventing.fire("formMember");
    },
    created() {
        Eventing.fire("show-upload");
        this.lang = document.documentElement.lang;
    },
    props: ["edit", "roles"],
    data() {
        return {
            form: {
                email: null,
                name: null,
            },
            loading: false,
            errors: null,
            debounce: null,
            lang: "nl",
        };
    },
    methods: {
        triggerFileUpload(field) {
            document.getElementById(field).click();
        },
        handleFileUpload(field) {
            this.file = this.$refs.logo.files[0];
            var self = this;
            let formData = new FormData();
            // formData.append("bestand", this.file);
            formData.append("photo", this.file);
            formData.append("photo-upload", true);

            var url = "/api/avatar";
            if (this.edit.hash && this.edit.hash != "undefined") {
                url = "/api/avatar/" + this.edit.hash;
            }

            axios({
                method: "post",
                url: url,
                data: formData,
            })
                .then(function (response) {
                    console.info(response.data);
                    Eventing.fire("notify", {
                        title: self.t("form-member.saved-photo", "Gelukt"),
                        message: self.t(
                            "form-member.saved-photo-title",
                            "Foto opgeslagen"
                        ),
                        type: "success",
                    });
                    self.edit.avatar = response.data;
                    self.form.avatar = response.data;
                    Eventing.fire("fetchMembers");
                })
                .catch((error) => {
                    console.error(error);
                    if (error.response) {
                        console.info(error.response.data);
                    }
                })
                .finally(function () {});
        },
        checkIfInGroup(id, type) {
            if (this.form.groups && type == "groups") {
                return Object.values(this.form.groups).includes(parseInt(id));
            }
            if (this.form.membertypes && type == "members") {
                return Object.values(this.form.membertypes).includes(
                    parseInt(id)
                );
            }
        },
        debounceSearchAddress(event) {
            let self = this;
            clearTimeout(this.debounce);
            this.debounce = setTimeout(() => {
                self.findAddress();
            }, 300);
        },
        closeModal() {
            Eventing.fire("closeModal");
        },
        getError(error, text) {
            try {
                if (this.errors) {
                    let errors = Object.values(this.errors);
                    if (text) {
                        return this.errors[error].toString();
                    }
                    return this.errors[error].toString() ? true : false;
                }
            } catch (error) {
                return false;
            }
            return false;
        },
        forceUpper(e, obj, prop) {
            const start = e.target.selectionStart;
            e.target.value = e.target.value.toUpperCase();
            this.$set(obj, prop, e.target.value);
            e.target.setSelectionRange(start, start);
        },
        saveForm() {
            let self = this;
            this.loading = true;
            var url = "/api/modules/members";
            var method = "post";
            if (this.edit.hash && this.edit.hash != "undefined") {
                url = "/api/modules/members/" + this.edit.hash;
                method = "put";
            }
            window.scrollTo({ top: 0, behavior: "smooth" });
            axios({
                method: method,
                url: url,
                data: this.form,
            })
                .then((response) => {
                    this.loading = false;
                    Eventing.fire("fetchMembers");
                    if (this.form.delete) {
                        Eventing.fire("notify", {
                            title: self.t("members.saved.deleted", "Gelukt"),
                            message: self.t(
                                "members.saved.delete",
                                "Lid delete"
                            ),
                        });
                        return (window.location.href = "/app#/m/members");
                    } else {
                        Eventing.fire("notify", {
                            title: self.t("members.saved.success", "Gelukt"),
                            message: self.t(
                                "members.saved." + method,
                                "Lid " + method
                            ),
                            type: "success",
                        });
                        this.$router.push({
                            name: "members-edit",
                            params: { id: response.data.data.hash },
                            query: { timestamp: Math.floor(100) },
                        });
                        this.form = response.data.data;
                    }
                })
                .catch((error) => {
                    if (error.response) {
                        if (error.response.status == 404) {
                            this.$router.push("/login");
                        } else if (error.response.status == 422) {
                            this.errors = error.response.data.errors;
                        } else if (error.response.status == 409) {
                        }
                    }
                    this.loading = false;
                });
            setTimeout(() => {
                window.scrollTo({ top: 0, behavior: "smooth" });
            }, 1000);
        },

        findAddress() {
            let self = this;
            try {
                var postcode = null;
                var housenumber = null;

                if (this.form.address?.postcode) {
                    postcode = this.form.address?.postcode
                        .replace(" ", "")
                        .toUpperCase();
                    this.form.address.postcode = postcode;
                    housenumber = this.form.address.housenumber;
                }
                if (postcode && postcode.length >= 6) {
                    axios
                        .put("/api/modules/address-by-postcode", {
                            postcode: postcode,
                            housenumber: housenumber,
                        })
                        .then((response) => {
                            let address = response.data.address;
                            if (address) {
                                this.form.address.street = address.street;
                                this.form.address.city = address.town;
                                if (!address.house_no) {
                                    this.form.address.housenumber = null;
                                }
                                if (!address.street) {
                                    if (self.lang == "nl") {
                                        Eventing.fire("notify", {
                                            title: self.t(
                                                "members.postcode.unknown-title",
                                                "Geen adres gevodnen"
                                            ),
                                            message: self.t(
                                                "members.postcode.unknown-text",
                                                "Met de ingevoerde postcode en/of huisnummer gegegevens is geen adres bekend in Nederland"
                                            ),
                                            type: "error",
                                        });
                                    }
                                }
                                // this.form.address.housenumber =
                                //     address.house_no;
                            }
                        })
                        .catch((error) => {
                            if (error.response) {
                                if (error.response.status == 409) {
                                }
                                if (error.response.status == 404) {
                                }
                            }
                        });
                }
            } catch (error) {
                console.error(error);
            }
        },
        setModal(role) {
            Eventing.fire("openRolesModal", role);
        },
        checkDelete() {
            var val = this.form.delete;
            if (val == true) {
                Eventing.fire("warning");
            } else {
                Eventing.fire("noWarning");
            }
        },
    },
    watch: {},
};
</script>
